import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { FileText, Download, Upload, CheckCircle, Mail } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '../../api/client';
import { downloadInvoice } from '../../utils/invoiceGenerator';
import { downloadPRD } from '../../utils/prdGenerator';
import { downloadContract } from '../../utils/contractGenerator';
import TopAlert from '../TopAlert';
import { useAuth } from '../../contexts/AuthContext';
import { getCurrencySymbol } from '../../utils/currency';

const ProjectDetailModal = ({ open, onClose, project, onSuccess }) => {
  const { user } = useAuth();
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentError, setDocumentError] = useState('');
  
  // Document generation inputs
  const [showPRDModal, setShowPRDModal] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [prdInputs, setPrdInputs] = useState({
    features: '',
    duration: '',
    customClause: ''
  });
  const [contractInputs, setContractInputs] = useState({
    duration: '',
    budget: '',
    customClause: ''
  });

  const fetchDocumentData = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/projects/${project.project_id}/documents`);
      setDocumentData(response.data);
      setDocumentError('');
    } catch (error) {
      setDocumentData(null);
      setDocumentError('Failed to load project documents. Please try again.');
      toast.error('Failed to load project documents');
    } finally {
      setLoading(false);
    }
  }, [project]);

  useEffect(() => {
    if (open && project) {
      fetchDocumentData();
    }
  }, [open, project, fetchDocumentData]);

  const handleGenerateInvoice = (incomeRecord) => {
    if (!documentData) return;
    
    const invoiceData = {
      invoiceNumber: `INV-${Date.now()}`,
      invoiceDate: new Date().toLocaleDateString(),
      dueDate: incomeRecord.expected_date || new Date(Date.now() + 30*24*60*60*1000).toLocaleDateString(),
      freelancer: documentData.freelancer,
      client: documentData.client,
      project: documentData.project,
      incomeRecord: incomeRecord
    };
    
    downloadInvoice(invoiceData);
    toast.success('Invoice generated successfully!');
  };

  const handleSendInvoiceEmail = async (incomeRecord) => {
    setLoading(true);
    try {
      await apiClient.post(`/income/${incomeRecord.income_id}/send-email`);
      toast.success('Invoice email sent to client!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invoice email');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePRD = () => {
    setShowPRDModal(true);
  };
  
  const generatePRDWithInputs = () => {
    if (!documentData) return;
    
    const prdData = {
      project: {
        ...documentData.project,
        custom_features: prdInputs.features,
        duration: prdInputs.duration,
        custom_clause: prdInputs.customClause
      },
      client: documentData.client,
      freelancer: documentData.freelancer
    };
    
    downloadPRD(prdData);
    toast.success('PRD generated successfully!');
    setShowPRDModal(false);
    setPrdInputs({ features: '', duration: '', customClause: '' });
  };

  const handleGenerateContract = () => {
    setShowContractModal(true);
  };
  
  const generateContractWithInputs = () => {
    if (!documentData) return;
    
    const contractData = {
      project: {
        ...documentData.project,
        total_budget: contractInputs.budget || documentData.project?.total_budget,
        duration: contractInputs.duration,
        custom_clause: contractInputs.customClause
      },
      client: documentData.client,
      freelancer: documentData.freelancer
    };
    
    downloadContract(contractData);
    toast.success('Contract generated successfully!');
    setShowContractModal(false);
    setContractInputs({ duration: '', budget: '', customClause: '' });
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUploadContract = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      
      await apiClient.post(`/projects/${project.project_id}/contract`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Contract uploaded! Project status changed to Ongoing');
      setSelectedFile(null);
      fetchDocumentData();
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload contract');
    } finally {
      setUploading(false);
    }
  };

  const projectData = documentData?.project;
  const client = documentData?.client;
  const income_records = documentData?.income_records || [];

  return (
    <>
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" data-testid="project-detail-modal">
        <DialogHeader>
          <DialogTitle>{projectData?.title || 'Project Details'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <TopAlert message={documentError} tone="error" onClose={() => setDocumentError('')} />
          {loading && (
            <div className="flex items-center justify-center py-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          )}

          {!loading && !documentData && (
            <div className="text-sm text-slate-500">Document data is unavailable.</div>
          )}

          {!loading && documentData && (
          <>
          {/* Project Info */}
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="text-base">Project Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex justify-between">
                <span className="text-slate-500">Client:</span>
                <span className="font-medium">{client.name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Status:</span>
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  projectData.status === 'completed' ? 'bg-emerald-100 text-emerald-800' :
                  projectData.status === 'ongoing' ? 'bg-blue-100 text-blue-800' :
                  'bg-slate-100 text-slate-800'
                }`}>
                  {projectData.status}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Budget:</span>
                <span className="font-medium">{getCurrencySymbol(user?.currency)}{projectData?.total_budget.toFixed(2)}</span>
              </div>
            </CardContent>
          </Card>

          {/* Generate Documents */}
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="text-base">Generate Documents</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={handleGeneratePRD}
              >
                <FileText className="h-4 w-4 mr-2" />
                Generate Project Requirement Document (PRD)
              </Button>
              
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={handleGenerateContract}
              >
                <FileText className="h-4 w-4 mr-2" />
                Generate Contract
              </Button>
            </CardContent>
          </Card>

          {/* Invoices */}
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="text-base">Generate Invoices</CardTitle>
            </CardHeader>
            <CardContent>
              {income_records && income_records.length > 0 ? (
                <div className="space-y-2">
                  {income_records.map(record => (
                    <div
                      key={record.income_id}
                      className="flex items-center justify-between p-3 rounded-lg bg-slate-50"
                    >
                      <div>
                        <p className="font-medium">{getCurrencySymbol(user?.currency)}{record.amount.toFixed(2)}</p>
                        <p className="text-xs text-slate-500">{record.notes}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          record.payment_status === 'received' ? 'bg-emerald-100 text-emerald-800' :
                          'bg-amber-100 text-amber-800'
                        }`}>
                          {record.payment_status}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleGenerateInvoice(record)}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Invoice
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-indigo-200 text-indigo-600 hover:bg-indigo-50"
                          onClick={() => handleSendInvoiceEmail(record)}
                          disabled={loading}
                        >
                          <Mail className="h-4 w-4 mr-1" />
                          Mail
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-500 text-sm">No payment records yet</p>
              )}
            </CardContent>
          </Card>
          </>
          )}

          {/* Upload Contract */}
          <Card className="border-slate-100">
            <CardHeader>
              <CardTitle className="text-base">Upload Signed Contract</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {projectData?.contract_url ? (
                <div className="flex items-center space-x-2 p-3 bg-emerald-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-emerald-900">Contract Uploaded</p>
                    <a
                      href={projectData.contract_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-emerald-600 hover:underline"
                    >
                      View Contract
                    </a>
                  </div>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="contract">Select signed contract PDF</Label>
                    <Input
                      id="contract"
                      type="file"
                      accept=".pdf,.doc,.docx"
                      onChange={handleFileSelect}
                      className="bg-slate-50"
                    />
                    {selectedFile && (
                      <p className="text-sm text-slate-600">
                        Selected: {selectedFile.name}
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={handleUploadContract}
                    disabled={!selectedFile || uploading}
                    className="w-full bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? 'Uploading...' : 'Upload Contract & Set Status to Ongoing'}
                  </Button>
                  <p className="text-xs text-slate-500">
                    Note: Uploading the signed contract will automatically change the project status to "Ongoing"
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>

    {/* PRD Input Modal */}
    <Dialog open={showPRDModal} onOpenChange={setShowPRDModal}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Generate PRD</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="prd-features">Key Features (optional)</Label>
            <Input
              id="prd-features"
              placeholder="List main features..."
              value={prdInputs.features}
              onChange={(e) => setPrdInputs({ ...prdInputs, features: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="prd-duration">Project Duration (optional)</Label>
            <Input
              id="prd-duration"
              placeholder="e.g. 3 months"
              value={prdInputs.duration}
              onChange={(e) => setPrdInputs({ ...prdInputs, duration: e.target.value })}
            />
          </div>
          <Button onClick={generatePRDWithInputs} className="w-full bg-indigo-600 hover:bg-indigo-700">
            Generate PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Contract Input Modal */}
    <Dialog open={showContractModal} onOpenChange={setShowContractModal}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Generate Contract</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-4">
          <div className="space-y-2">
            <Label htmlFor="contract-duration">Project Duration</Label>
            <Input
              id="contract-duration"
              placeholder="e.g. 3 months"
              value={contractInputs.duration}
              onChange={(e) => setContractInputs({ ...contractInputs, duration: e.target.value })}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="contract-budget">Custom Budget (defaults to project budget)</Label>
            <Input
              id="contract-budget"
              type="number"
              placeholder={projectData?.total_budget}
              value={contractInputs.budget}
              onChange={(e) => setContractInputs({ ...contractInputs, budget: e.target.value })}
            />
          </div>
          <Button onClick={generateContractWithInputs} className="w-full bg-indigo-600 hover:bg-indigo-700">
            Generate PDF
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ProjectDetailModal;
